import { Container, Graphics, Ticker } from "pixi.js";

/**
 * The layer that was missing.
 *
 * Every game here was a correct cognitive task and a joyless one: a shape changed colour and
 * that was the entire response to a child's action. This module is the difference between
 * "the tile turned green" and "the tile answered you" — squash, bounce, ripple, particles,
 * light. None of it is decoration; for a six-year-old it IS the feedback, and a product whose
 * Phase 0 verdict rests on engagement cannot treat it as polish to add later.
 *
 * **What this may and may not touch.** Everything here fires on the child's action or after
 * their response. None of it alters what was PRESENTED, so none of it changes the instrument
 * or requires a calibration bump. That boundary is the reason feedback can be as rich as we
 * can make it while a stimulus change has to be declared.
 *
 * **Reduced motion is honoured.** Some children are motion-sensitive, and a task that makes a
 * child feel unwell is not measuring what it thinks it is. With the preference set, colour and
 * sound feedback stay and movement stops — the information is never in the motion alone.
 */

export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ---------------------------------------------------------------------------
// Easing. Named, because "which curve" is the whole difference between a move
// that feels mechanical and one that feels alive.
// ---------------------------------------------------------------------------

/** Overshoots and settles. What makes a tile feel like it has weight. */
export function easeOutBack(t: number): number {
  const c = 1.70158;
  return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
}

/** Fast then gentle. The default for anything appearing. */
export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/** Slow at both ends. For anything a child's eye has to follow. */
export const easeInOutQuad = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

/**
 * Runs `onFrame` from 0 to 1 over `durationMs`, then resolves.
 *
 * Driven by Pixi's shared ticker rather than by `setInterval`, so animation is tied to the
 * render loop and cannot drift out of step with what is on screen. Every animation returns a
 * promise so a task's sequence reads as a sequence rather than as nested callbacks.
 */
export function animate(
  durationMs: number,
  onFrame: (t: number) => void,
): Promise<void> {
  if (durationMs <= 0) {
    onFrame(1);
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let elapsed = 0;
    const tick = (ticker: Ticker) => {
      elapsed += ticker.deltaMS;
      const t = Math.min(1, elapsed / durationMs);
      try {
        onFrame(t);
      } catch (err) {
        /**
         * One broken effect must not stop every animation in the app.
         *
         * These callbacks run inside Pixi's shared ticker, and a throw there propagates out of the
         * frame loop — so a single effect touching something it should not took down rendering for
         * everything, and the next game a child opened was drawn half-finished until they reloaded.
         * That is exactly what happened: `glow` kept drawing into a Graphics its trial had already
         * destroyed.
         *
         * The guards in each effect are the real fix; this is the net under them. It is deliberately
         * loud rather than silent — a swallowed exception here would hide the next such bug
         * completely, and the symptom is far too indirect to find twice.
         */
        Ticker.shared.remove(tick);
        console.error("animation frame failed and was stopped", err);
        resolve();
        return;
      }
      if (t >= 1) {
        Ticker.shared.remove(tick);
        resolve();
      }
    };
    Ticker.shared.add(tick);
  });
}

/**
 * Sleeps, and gives up early if the child left.
 *
 * The signal matters more than it looks. A task spends most of its time in these waits —
 * presenting a sequence, holding a shape on screen, pausing between trials — and without the
 * signal an abort was only noticed at the next response, so a game kept animating and sounding
 * for seconds after the child pressed back, while the next game was already starting on top of
 * it. Resolves rather than rejects, so the caller's own abort check decides what to do.
 */
export const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        resolve();
      },
      { once: true },
    );
  });

/** Returned instead of a response when the child left the game. */
export const ABANDONED = Symbol("abandoned");

/**
 * Races a promise against the child leaving.
 *
 * A trial waits for a tap that may never come — the child presses back, or closes the app. Without
 * this the awaiting promise never settles, so the task never records a `task_end` and the session
 * says the child is still playing. `reason: "abandoned"` was in the result type from the start and
 * no code path could produce it.
 *
 * Losing the race does not cancel the tap handler; the caller's `cleanup` does that. This only
 * unblocks the loop.
 */
export function untilAbandoned<T>(
  promise: Promise<T>,
  signal: AbortSignal | undefined,
): Promise<T | typeof ABANDONED> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.resolve(ABANDONED);
  return Promise.race([
    promise,
    new Promise<typeof ABANDONED>((resolve) => {
      signal.addEventListener("abort", () => resolve(ABANDONED), { once: true });
    }),
  ]);
}

/** The child stopped answering. Distinct from leaving, which they did deliberately. */
export const TIMED_OUT = Symbol("timed-out");

/**
 * Races a trial against the child leaving AND against them stopping.
 *
 * `trialTimeoutMs` has been declared on every task since the first bundle and enforced by nothing:
 * `trial_timeout` was defined in the protocol, handled by the validity assessor, and emitted by no
 * code path at all. A child who put the phone down mid-trial sat in a promise that never settled, so
 * the task recorded no `task_end` and the session said they were still playing — forever.
 *
 * **A timeout ends the SITTING, not the trial, and is recorded as abandonment rather than as a wrong
 * answer.** That distinction is the whole reason this is not simply scored as an error: we know the
 * child stopped, and we do not know that they could not do it. Scoring silence as failure is the same
 * mistake `stopAfterErrors` made — recording a state of the room as a fact about a mind.
 *
 * Losing the race does not cancel the tap handler; the caller's `cleanup` does that.
 */
export function untilSettled<T>(
  promise: Promise<T>,
  signal: AbortSignal | undefined,
  timeoutMs: number,
): Promise<T | typeof ABANDONED | typeof TIMED_OUT> {
  if (signal?.aborted) return Promise.resolve(ABANDONED);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const stopped = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
  });
  const left = new Promise<typeof ABANDONED>((resolve) => {
    signal?.addEventListener("abort", () => resolve(ABANDONED), { once: true });
  });

  // The timer is cleared however the race ends, or a finished trial leaves a pending timeout that
  // keeps the page awake and fires into a torn-down renderer.
  return Promise.race([promise, stopped, left]).finally(() => clearTimeout(timer));
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

/**
 * A quick scale bounce, anchored at the object's centre.
 *
 * Requires the target's `pivot` to be at its centre; a top-left pivot scales the shape away
 * from the finger that just touched it, which reads as the object recoiling from the child.
 */
export async function pop(
  target: Container,
  { scale = 1.18, durationMs = 260 } = {},
): Promise<void> {
  if (prefersReducedMotion()) return;
  const base = target.scale.x;
  await animate(durationMs, (t) => {
    if (target.destroyed) return;
    // Up fast, back with an overshoot: the shape looks springy rather than inflated.
    const curve = t < 0.35 ? easeOutCubic(t / 0.35) : 1 - easeOutBack((t - 0.35) / 0.65);
    target.scale.set(base * (1 + (scale - 1) * curve));
  });
  if (!target.destroyed) target.scale.set(base);
}

/**
 * An expanding ring where the child touched.
 *
 * The cheapest possible acknowledgement that a tap landed, and the most important: without
 * it, a tap that the game is still processing looks like a tap that was ignored, and a child
 * taps again — which the contamination detectors then see as a multi-touch anomaly.
 */
export function ripple(
  layer: Container,
  x: number,
  y: number,
  color: number,
  radius = 60,
): void {
  if (prefersReducedMotion()) return;
  const ring = new Graphics();
  layer.addChild(ring);
  void animate(420, (t) => {
    // A trial can end mid-effect: the layer is destroyed with its children while this is still
    // animating. Drawing into a destroyed Graphics throws inside the ticker.
    if (ring.destroyed) return;
    const eased = easeOutCubic(t);
    ring.clear();
    ring.circle(x, y, radius * eased).stroke({ width: 3 * (1 - eased), color, alpha: 1 - eased });
  }).then(() => {
    if (!ring.destroyed) ring.destroy();
  });
}

/**
 * A spray of particles. The reward.
 *
 * Deliberately generous on a correct answer and absent on a wrong one. Celebration is not
 * symmetric with correction here: v2 §19 warns against frustration being recorded as
 * inability, and a game that punishes a wrong answer with an effect teaches a child to stop
 * trying, which corrupts the measurement as surely as it hurts them.
 */
export function burst(
  layer: Container,
  x: number,
  y: number,
  colors: readonly number[],
  count = 14,
): void {
  if (prefersReducedMotion()) return;
  for (let i = 0; i < count; i++) {
    const dot = new Graphics();
    const color = colors[i % colors.length] ?? 0xffffff;
    const size = 3 + Math.random() * 4;
    dot.circle(0, 0, size).fill(color);
    dot.position.set(x, y);
    layer.addChild(dot);

    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
    const distance = 40 + Math.random() * 70;
    // Gravity, so particles arc and fall instead of sliding outward in a flat star.
    void animate(560 + Math.random() * 220, (t) => {
      if (dot.destroyed) return;
      const eased = easeOutCubic(t);
      dot.position.set(
        x + Math.cos(angle) * distance * eased,
        y + Math.sin(angle) * distance * eased + 60 * t * t,
      );
      dot.alpha = 1 - t;
      dot.scale.set(1 - 0.4 * t);
    }).then(() => {
      if (!dot.destroyed) dot.destroy();
    });
  }
}

/**
 * A soft glow behind an object, fading in and out.
 *
 * Concentric translucent circles rather than a blur filter: filters cost a render target per
 * object, and the frame budget on a low-end phone is an ADR-002 exit criterion.
 */
export function glow(
  layer: Container,
  x: number,
  y: number,
  radius: number,
  color: number,
  durationMs = 520,
): void {
  const halo = new Graphics();
  layer.addChildAt(halo, 0);
  void animate(durationMs, (t) => {
    // The one that actually broke the app: a glow outliving the trial that started it.
    if (halo.destroyed) return;
    const fade = t < 0.3 ? t / 0.3 : 1 - (t - 0.3) / 0.7;
    halo.clear();
    for (let ring = 3; ring >= 1; ring--) {
      halo.circle(x, y, radius * (1 + ring * 0.22)).fill({
        color,
        alpha: 0.1 * fade * (4 - ring),
      });
    }
  }).then(() => {
    if (!halo.destroyed) halo.destroy();
  });
}

/**
 * A brief wobble, for a wrong answer.
 *
 * Small and quick on purpose. This is the one negative effect in the module, and it says
 * "not that one" rather than "you failed" — the difference matters for a child who is about
 * to decide whether to keep going.
 */
export async function nudge(target: Container, amplitude = 7): Promise<void> {
  if (prefersReducedMotion()) return;
  const baseX = target.position.x;
  await animate(300, (t) => {
    if (target.destroyed) return;
    const decay = 1 - t;
    target.position.x = baseX + Math.sin(t * Math.PI * 6) * amplitude * decay;
  });
  if (!target.destroyed) target.position.x = baseX;
}

/**
 * Draws a rounded tile with a lit top edge.
 *
 * Here rather than in each task because "what a tappable thing looks like" should be one
 * answer. The inner highlight is what stops a flat rectangle from reading as a placeholder.
 */
export function drawTile(
  g: Graphics,
  size: number,
  color: number,
  { lit = false, radius = size * 0.22 } = {},
): void {
  g.clear();
  g.roundRect(-size / 2, -size / 2, size, size, radius).fill(color);
  // A brighter band along the top: a single cheap cue that the surface has volume.
  g.roundRect(-size / 2 + size * 0.1, -size / 2 + size * 0.08, size * 0.8, size * 0.22, radius * 0.6)
    .fill({ color: 0xffffff, alpha: lit ? 0.35 : 0.07 });
}
